import { Typography } from '@/ui/atoms/typography/Typography'
import { StepJourney } from '@/ui/molecules/three-step-journey/StepJourney'
import { cn } from '@/ui/utils/style'
import type { ReactNode } from 'react'
import { PageHeroStats, type PageHeroStat } from './PageHeroStats'

export type { PageHeroStat }

export interface PageHeroProps {
  title: string
  titleAdornment?: ReactNode
  subtitle?: string
  journey?: string[] | ReactNode
  stats?: PageHeroStat[]
  statsLoading?: boolean
  statsSlot?: ReactNode
  pillNav?: ReactNode
  className?: string
}

function isStepsArray(value: string[] | ReactNode): value is string[] {
  return Array.isArray(value)
}

export function PageHero({
  title,
  titleAdornment,
  subtitle,
  journey,
  stats,
  statsLoading = false,
  statsSlot,
  pillNav,
  className,
}: PageHeroProps) {
  // biome-ignore lint/complexity/useOptionalChain: <explanation>
  const hasStats = stats !== undefined && stats.some((s) => !s.hidden)

  const journeyElement = journey ? (
    isStepsArray(journey) ? (
      <StepJourney steps={journey} className="flex" />
    ) : (
      journey
    )
  ) : null

  return (
    <div
      className={cn(
        'relative flex flex-col gap-2 sm:gap-4',
        'mb-8 overflow-hidden rounded-lg bg-gradient-to-br from-gradient-yellow/5 via-background to-gradient-green/5 p-6 md:px-10 md:py-8',
        className,
      )}
    >
      <div className="-top-24 -right-24 absolute h-48 w-48 rounded-full bg-gradient-to-br from-gradient-yellow/20 to-gradient-green/20 blur-[80px]" />
      <div className="-bottom-24 -left-24 absolute h-48 w-48 rounded-full bg-gradient-to-br from-gradient-green/20 to-gradient-yellow/20 blur-[80px]" />

      {/* Desktop: Row 1 (title/subtitle left, stats right) + Row 2 (journey left, nav right) */}
      {/* Mobile: title → subtitle → steps → stats → nav */}

      {/* Row 1: Title + subtitle (left), stats (right on desktop) */}
      <div className="flex animate-fade-in-slow flex-col gap-2 opacity-0 md:flex-row md:items-end md:justify-between sm:gap-4">
        <div className="flex flex-col gap-1 sm:gap-4">
          <div className="flex items-center gap-2">
            <Typography variant="h1" className="text-3xl lg:text-6xl sm:text-5xl" gradient>
              {title}
            </Typography>
            {titleAdornment}
          </div>
          {subtitle && <p className="max-w-xl text-muted-foreground text-sm sm:text-lg">{subtitle}</p>}
        </div>
        {(hasStats || statsSlot) && (
          <div className="hidden flex-col items-end gap-4 md:flex">
            {statsSlot}
            {hasStats && <PageHeroStats stats={stats} loading={statsLoading} />}
          </div>
        )}
      </div>

      {/* Mobile: steps → stats → nav stacked */}
      <div className="flex flex-col gap-2 md:hidden">
        {(hasStats || statsSlot) && (
          <div className="mt-2 animate-fade-in-slow opacity-0" style={{ animationDelay: '120ms' }}>
            {statsSlot}
            {hasStats && <PageHeroStats stats={stats} loading={statsLoading} />}
          </div>
        )}
        {pillNav && (
          <div className="mt-2 animate-fade-in-slow opacity-0 md:mt-0" style={{ animationDelay: '150ms' }}>
            {pillNav}
          </div>
        )}
      </div>

      {/* Desktop: Row 2 — journey left, nav right */}
      {(journeyElement || pillNav) && (
        <div
          className="hidden min-h-10 animate-fade-in-slow items-center justify-between opacity-0 md:flex"
          style={{ animationDelay: '150ms' }}
        >
          <div>{journeyElement}</div>
          <div className="ml-auto">{pillNav}</div>
        </div>
      )}
    </div>
  )
}
